import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  FileText,
  Shield,
  ClipboardCheck,
  Wrench,
  ArrowRight,
  Clock,
  QrCode,
  Download,
  X,
} from "lucide-react";

// Each app card = { key, title, subtitle, description, icon, colorClasses, href|comingSoon }
const APPS = [
  {
    key: "underlag",
    title: "Worksheet",
    subtitle: "Timesheet & work order",
    description:
      "Log hours, materials and toll crossings. Send to the client for digital signature.",
    Icon: FileText,
    accent: "cyan",
    href: "/underlag",
  },
  {
    key: "sja",
    title: "SJA",
    subtitle: "Safe Job Analysis",
    description:
      "Map risks before work starts. Involve the whole team.",
    Icon: Shield,
    accent: "amber",
    href: "/apps/sja",
  },
  {
    key: "kontroll",
    title: "Inspection",
    subtitle: "Control forms",
    description:
      "Weekly checks of scaffolding, safety gear and materials on site.",
    Icon: ClipboardCheck,
    accent: "indigo",
    href: "/apps/kontroll",
  },
  {
    key: "utstyr",
    title: "Equipment",
    subtitle: "Equipment log",
    description:
      "Register use, damage and maintenance of tools and machines.",
    Icon: Wrench,
    accent: "emerald",
    comingSoon: true,
  },
];

// Tailwind color tokens per accent (kept explicit so JIT builds keep the class)
const accentMap = {
  cyan: {
    ring: "hover:border-cyan-400/60 hover:shadow-cyan-500/20",
    iconBg: "bg-cyan-500/15 text-cyan-300 border-cyan-400/40",
    cta: "bg-cyan-500 hover:bg-cyan-400 text-slate-900",
    dot: "bg-cyan-400",
  },
  amber: {
    ring: "hover:border-amber-400/60 hover:shadow-amber-500/15",
    iconBg: "bg-amber-500/15 text-amber-300 border-amber-400/40",
    cta: "bg-amber-500 text-slate-900",
    dot: "bg-amber-400",
  },
  indigo: {
    ring: "hover:border-indigo-400/60 hover:shadow-indigo-500/15",
    iconBg: "bg-indigo-500/15 text-indigo-300 border-indigo-400/40",
    cta: "bg-indigo-500 text-white",
    dot: "bg-indigo-400",
  },
  emerald: {
    ring: "hover:border-emerald-400/60 hover:shadow-emerald-500/15",
    iconBg: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
    cta: "bg-emerald-500 text-slate-900",
    dot: "bg-emerald-400",
  },
};

function AppCard({ app }) {
  const c = accentMap[app.accent] || accentMap.cyan;
  const { Icon } = app;
  const inner = (
    <div
      className={`group relative flex flex-col h-full bg-slate-900/60 border border-slate-700/60 ${c.ring} rounded-2xl p-5 sm:p-6 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl ${app.comingSoon ? "opacity-90" : ""}`}
      data-testid={`app-card-${app.key}`}
    >
      {app.comingSoon && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-800/80 border border-slate-600 text-[10px] uppercase tracking-wider text-slate-300 font-semibold">
          <Clock className="w-3 h-3" /> Coming soon
        </div>
      )}
      <div className={`w-12 h-12 rounded-xl border ${c.iconBg} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6" strokeWidth={1.8} />
      </div>
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-xl sm:text-2xl font-bold text-white">{app.title}</h3>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      </div>
      <p className="text-[13px] uppercase tracking-widest text-slate-400 font-semibold mb-3">
        {app.subtitle}
      </p>
      <p className="text-sm text-slate-300 leading-relaxed flex-1">{app.description}</p>
      <div className="mt-5">
        {app.comingSoon ? (
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/60 text-slate-500 text-sm font-medium border border-slate-700 cursor-not-allowed">
            Coming soon
          </div>
        ) : (
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${c.cta} font-semibold text-sm shadow-md transition-transform group-hover:translate-x-0.5`}
          >
            Open <ArrowRight className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );

  if (app.comingSoon) return inner;
  return (
    <Link to={app.href} className="block h-full" data-testid={`app-link-${app.key}`}>
      {inner}
    </Link>
  );
}

export default function AppsHub() {
  const [showQr, setShowQr] = useState(false);
  const qrWrapperRef = useRef(null);
  const appsUrl = typeof window !== "undefined" ? `${window.location.origin}/apps` : "/apps";

  const downloadQr = () => {
    const canvas = qrWrapperRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "doserne-apps-qr.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
      {/* Ambient decorative glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 w-96 h-96 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute top-40 -right-32 w-96 h-96 rounded-full bg-indigo-500/10 blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-16">
        {/* Header */}
        <header className="mb-10 sm:mb-14 flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-semibold uppercase tracking-wider">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              Doserne Stillas — Field tools
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-3">
              Apps<span className="text-cyan-400">.</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
              All forms and tools for on-site work. Open worksheets, report damage,
              write SJA — everything from one place, mobile-ready.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowQr(true)}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/70 hover:bg-slate-700 border border-slate-700 hover:border-cyan-500/50 text-cyan-300 text-xs sm:text-sm font-semibold transition"
            data-testid="show-qr-btn"
            title="Show QR code for printing"
          >
            <QrCode className="w-4 h-4" />
            <span className="hidden sm:inline">QR code</span>
          </button>
        </header>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
          {APPS.map((app) => (
            <AppCard key={app.key} app={app} />
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-14 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            © {new Date().getFullYear()} Doserne Stillas AS
          </div>
          <div className="flex items-center gap-4">
            <span className="opacity-70">Need help? Call 970 90 900</span>
          </div>
        </footer>
      </div>

      {/* QR Modal */}
      {showQr && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowQr(false)}
          data-testid="qr-modal"
        >
          <div
            className="bg-white text-slate-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowQr(false)}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 text-slate-500"
              data-testid="qr-modal-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h2 className="text-xl font-bold text-slate-900 mb-1">Scan for forms</h2>
              <p className="text-sm text-slate-500 mb-5">
                Doserne Stillas — Field tools
              </p>
              <div
                ref={qrWrapperRef}
                className="inline-block p-3 bg-white border-4 border-slate-900 rounded-xl"
              >
                <QRCodeCanvas
                  value={appsUrl}
                  size={220}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  level="H"
                  includeMargin={false}
                />
              </div>
              <p className="mt-3 text-[11px] font-mono text-slate-400 break-all">{appsUrl}</p>
              <button
                type="button"
                onClick={downloadQr}
                className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 rounded-lg font-semibold text-sm shadow-md transition"
                style={{ color: "#ffffff" }}
                data-testid="qr-download-btn"
              >
                <Download className="w-4 h-4" /> Download PNG
              </button>
              <p className="mt-3 text-xs text-slate-500">
                Print and hang in the warehouse or stick in the tool box.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
